import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Calendar } from '../../../components/calendar';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { addLocale } from 'primereact/api';
import { get } from 'lodash';
import { toast } from 'react-toastify';
import dateFormat from 'dateformat';

import 'react-toastify/dist/ReactToastify.css';

import * as actions from '../../../store/modules/auth/actions';
import { displayErrors } from '../../../util/diversos';
import { Input155px } from '../../../styles/inputGroup';
import store from '../../../store/store';
import axiosAdmin from '../../../services/axiosAdmin';
import Card from '../../../components/card';
import Loading from '../../loading';

const CODIGO_CONFIRMACAO = 'EU CONCORDO';

// Somente o login da própria empresa (sem operador)
export function podeExecutarLimpeza() {
  const { user, idOperador } = store.getState().auth;
  const acessoEmpresa = !idOperador || Number(idOperador) <= 0;
  return get(user, 'tipoUsuario', '') === 'EMPRESA' && acessoEmpresa;
}

export default function LimpezaPeriodo() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [listaGrupos, setListaGrupos] = useState([]);
  const [gruposSelecionados, setGruposSelecionados] = useState([]);
  const [dataInicial, setDataInicial] = useState(null);
  const [dataFinal, setDataFinal] = useState(null);
  const [codigoConfirmacao, setCodigoConfirmacao] = useState('');
  const [mostrarConfirmacao, setMostrarConfirmacao] = useState(false);
  const [resultados, setResultados] = useState([]);

  addLocale('pt-BR', {
    firstDayOfWeek: 0,
    dayNames: ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'],
    dayNamesShort: ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'],
    dayNamesMin: ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'],
    monthNames: ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'],
    monthNamesShort: ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'],
    today: 'hoje',
    clear: 'Limpar',
  });

  useEffect(() => {
    const newTitle = 'Limpeza de Tabelas por Período';
    dispatch(actions.titleHeaderChange({ newTitle }));

    if (!podeExecutarLimpeza()) {
      toast.info('Limpeza permitida somente para o login da própria empresa.');
      navigate('/');
      return;
    }

    async function getGrupos() {
      try {
        setIsLoading(true);
        const { data } = await axiosAdmin.get('/limpezaperiodo/grupos');
        setListaGrupos(data);
      } catch (err) {
        displayErrors(err, dispatch, navigate, '/', 'Não foi possível carregar os grupos de tabelas');
      } finally {
        setIsLoading(false);
      }
    }
    getGrupos();
  }, [dispatch, navigate]);

  const handleGrupo = (codigo, checked) => {
    if (checked) {
      setGruposSelecionados([...gruposSelecionados, codigo]);
    } else {
      setGruposSelecionados(gruposSelecionados.filter(item => item !== codigo));
    }
  };

  const handleMarcarTodos = (checked) => {
    setGruposSelecionados(checked ? listaGrupos.map(grupo => grupo.codigo) : []);
  };

  const codigoValido = codigoConfirmacao.trim() === CODIGO_CONFIRMACAO;

  const validar = () => {
    if (!dataInicial || !dataFinal) {
      toast.warning('Informe a data inicial e a data final.');
      return false;
    }
    if (dataFinal < dataInicial) {
      toast.warning('A data final não pode ser anterior à data inicial.');
      return false;
    }
    if (gruposSelecionados.length === 0) {
      toast.warning('Selecione ao menos um grupo de tabelas.');
      return false;
    }
    if (!codigoValido) {
      toast.warning(`Digite o código de confirmação: ${CODIGO_CONFIRMACAO}`);
      return false;
    }
    return true;
  };

  const handleExecutar = () => {
    if (validar()) {
      setMostrarConfirmacao(true);
    }
  };

  async function executarLimpeza() {
    setMostrarConfirmacao(false);
    try {
      setIsLoading(true);
      setResultados([]);
      const { data } = await axiosAdmin.post('/limpezaperiodo/executar', {
        dataInicial: dateFormat(dataInicial, 'dd-mm-yyyy'),
        dataFinal: dateFormat(dataFinal, 'dd-mm-yyyy'),
        grupos: gruposSelecionados,
        codigoConfirmacao: codigoConfirmacao.trim(),
        idOperador: Number(store.getState().auth.idOperador) || 0,
      });
      setResultados(data);
      setCodigoConfirmacao('');
      if (data.some(item => item.erro)) {
        toast.warning('Limpeza concluída com erros em alguns grupos. Veja o resultado abaixo.');
      } else {
        toast.success('Limpeza concluída.');
      }
    } catch (err) {
      displayErrors(err, dispatch, navigate, '', 'Não foi possível executar a limpeza');
    } finally {
      setIsLoading(false);
    }
  }

  const periodoTexto = () => (dataInicial && dataFinal
    ? `${dateFormat(dataInicial, 'dd/mm/yyyy')} a ${dateFormat(dataFinal, 'dd/mm/yyyy')}`
    : '');

  const confirmacaoFooter = (
    <div>
      <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={() => setMostrarConfirmacao(false)} />
      <Button label="Excluir definitivamente" icon="pi pi-trash" className="p-button-danger" onClick={executarLimpeza} />
    </div>
  );

  return (
    <>
      <Loading isLoading={isLoading} />
      <div className="card mb-3">
        <h6 className="card-header" style={{ textAlign: 'center' }}>Limpeza de Tabelas por Período</h6>
      </div>
      <Card>
        <div className="row form-elo">
          <div className="col-12">
            <div className="alert alert-danger" role="alert" style={{ marginBottom: '8px' }}>
              <i className="pi pi-exclamation-triangle" style={{ marginRight: '8px' }} />
              Os registros dos grupos selecionados, dentro do período, serão
              <strong> excluídos definitivamente</strong> de todas as empresas (CNPJs) deste login.
              Esta operação não pode ser desfeita. Faça um backup do banco de dados antes de continuar.
            </div>
          </div>
        </div>

        <div className="row form-elo">
          <div className="col-lg-4">
            <div className="p-inputgroup">
              <Input155px className="p-inputgroup-addon">Data inicial</Input155px>
              <Calendar
                showIcon
                mask="99/99/9999"
                dateFormat="dd/mm/yy"
                showButtonBar
                showOnFocus={false}
                locale="pt-BR"
                value={dataInicial}
                onChange={(e) => setDataInicial(e.value)}
              />
            </div>
          </div>
          <div className="col-lg-4">
            <div className="p-inputgroup">
              <Input155px className="p-inputgroup-addon">Data final</Input155px>
              <Calendar
                showIcon
                mask="99/99/9999"
                dateFormat="dd/mm/yy"
                showButtonBar
                showOnFocus={false}
                locale="pt-BR"
                value={dataFinal}
                onChange={(e) => setDataFinal(e.value)}
              />
            </div>
          </div>
        </div>

        <div className="row form-elo">
          <div className="col-12">
            <div className="field-checkbox" style={{ marginBottom: '8px' }}>
              <Checkbox
                inputId="marcarTodos"
                checked={listaGrupos.length > 0 && gruposSelecionados.length === listaGrupos.length}
                onChange={e => handleMarcarTodos(e.checked)}
              />
              <label htmlFor="marcarTodos" style={{ marginLeft: '8px', fontWeight: 'bold' }}>Marcar todos</label>
            </div>
          </div>
          {listaGrupos.map(grupo => (
            <div className="col-lg-6" key={grupo.codigo}>
              <div className="field-checkbox" style={{ marginBottom: '8px' }}>
                <Checkbox
                  inputId={`grupo_${grupo.codigo}`}
                  checked={gruposSelecionados.includes(grupo.codigo)}
                  onChange={e => handleGrupo(grupo.codigo, e.checked)}
                />
                <label htmlFor={`grupo_${grupo.codigo}`} style={{ marginLeft: '8px' }}>
                  {grupo.descricao}
                  <small style={{ display: 'block', opacity: 0.7, marginLeft: '0px' }}>
                    {grupo.raizes.map(raiz => raiz.descricao).join(', ')}
                  </small>
                </label>
              </div>
            </div>
          ))}
        </div>

        <div className="row form-elo">
          <div className="col-lg-8">
            <div className="p-inputgroup">
              <Input155px className="p-inputgroup-addon">Confirmação</Input155px>
              <InputText
                value={codigoConfirmacao}
                placeholder={`Digite ${CODIGO_CONFIRMACAO} para liberar a limpeza`}
                onChange={e => setCodigoConfirmacao(e.target.value.toUpperCase())}
              />
            </div>
          </div>
          <div className="col-lg-4">
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                className="btn btn-danger"
                style={{ borderRadius: '15px' }}
                disabled={!codigoValido}
                onClick={handleExecutar}
              >
                <i className="pi pi-trash" style={{ marginRight: '6px' }} />
                Executar limpeza
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ borderRadius: '15px' }}
                onClick={() => navigate('/')}
              >
                <i className="pi pi-times" style={{ marginRight: '6px' }} />
                Cancelar
              </button>
            </div>
          </div>
        </div>

        {resultados.length > 0 && (
          <div className="row form-elo">
            <div className="col-12">
              <table className="table table-sm table-striped">
                <thead>
                  <tr>
                    <th>Grupo</th>
                    <th>Detalhe</th>
                    <th style={{ textAlign: 'right' }}>Registros excluídos</th>
                  </tr>
                </thead>
                <tbody>
                  {resultados.map(resultado => (
                    <tr key={resultado.codigo}>
                      <td>{resultado.descricao}</td>
                      <td>
                        {resultado.erro
                          ? <span style={{ color: '#dc3545' }}>{`Erro (nada foi excluído neste grupo): ${resultado.erro}`}</span>
                          : Object.entries(resultado.registros || {}).map(([descricao, qtd]) => `${descricao}: ${qtd}`).join(' | ')}
                      </td>
                      <td style={{ textAlign: 'right' }}>{resultado.erro ? '-' : resultado.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>

      <Dialog
        header="Confirmar limpeza"
        visible={mostrarConfirmacao}
        style={{ width: '450px' }}
        modal
        footer={confirmacaoFooter}
        onHide={() => setMostrarConfirmacao(false)}
      >
        <p>
          Excluir definitivamente os registros de
          <strong>{` ${gruposSelecionados.length} grupo(s) `}</strong>
          no período
          <strong>{` ${periodoTexto()}`}</strong>
          ?
        </p>
        <p>Esta operação não pode ser desfeita.</p>
      </Dialog>
    </>
  );
}
